import React, { ReactNode, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { Link } from 'react-router-dom';
import { useAppSelector } from '@/utils/hooks';
import { SimplePageTitle } from '@/components/layout/PageTitle';
import { Tabs } from '@ygtechnologies/rubus-ui';
import { OptionGroupsPage } from './OptionGroupsPage';
import ProjectCatalogProducts from '../project-catalog/ProjectCatalogProducts';
import { DependenciesRouting } from '../buyer-options/Dependencies';
import BuyerOptionPresetsPage from '../buyer-option-presets/BuyerOptionPresetsPage';
import { Configurators3DPage } from './Configurators3D/Configurators3DPage';
import { Configurator3DMappingPage } from './Configurator3DMapping/Configurator3DMappingPage';
import { t } from '@lingui/core/macro';
import { OptionGroupLinkStatus } from '@/types';
import { ProjectRelativeRedirect } from '@/components/navigation';

type TabId = 'options' | 'tasks' | 'styles' | 'products' | 'dependencies' | '3d-configurators' | '3d-mapping' | 'testing-notes';
interface TabSetup {
    id: TabId;
    name: ReactNode;
    url: string;
    render(options: { projectId: number }): ReactNode;
    notificationCount?: number | null;
}

export interface OptionsPageProps {
    projectId: string | number;
    tabId: TabId;
}

export const OptionsPage: React.FC<OptionsPageProps> = ({ projectId, tabId }) => {
    const projectOverview = useAppSelector((state) => state.seller.projectOverview);
    // The 3D tabs only make sense for projects running a 3D configurator, so they follow the
    // project's use3dConfigurator setting (Project settings → General).
    const use3dConfigurator = useAppSelector((state) => state.global.activeProject?.use3dConfigurator ?? false);
    const tabs: TabSetup[] = useMemo(
        () => [
            {
                id: 'options',
                url: 'options',
                name: <Trans>All options</Trans>,
                render: () => <OptionGroupsPage key="all-options" />,
            },
            {
                id: 'tasks',
                url: 'tasks',
                name: <Trans>My tasks</Trans>,
                render: () => <OptionGroupsPage key="tasks" defaultPublishedStatusFilter={[OptionGroupLinkStatus.Pending]} publishedStatusFilterToggleValues={[OptionGroupLinkStatus.Pending, OptionGroupLinkStatus.Rejected]} />,
                notificationCount: projectOverview.tasks?.optionGroups || 0,
            },
            {
                id: 'styles',
                url: 'styles/Main',
                name: <Trans>Styles</Trans>,
                render: (options) => <BuyerOptionPresetsPage {...options} />,
            },
            {
                id: 'products',
                url: 'products',
                name: <Trans>Products</Trans>,
                render: (options) => <ProjectCatalogProducts />,
            },
            {
                id: 'dependencies',
                url: 'dependencies',
                name: <Trans>Dependencies</Trans>,
                render: (options) => <DependenciesRouting />,
            },
            {
                id: '3d-configurators',
                url: '3d-configurators',
                name: <Trans>3D Configurators</Trans>,
                render: (options) => <Configurators3DPage projectId={options.projectId} />,
            },
            {
                id: '3d-mapping',
                url: '3d-mapping',
                name: <Trans>3D Mapping</Trans>,
                render: (options) => <Configurator3DMappingPage projectId={options.projectId} />,
            },
        ],
        [projectOverview]
    );

    const visibleTabs = use3dConfigurator ? tabs : tabs.filter((t) => t.id !== '3d-configurators' && t.id !== '3d-mapping');

    const activeTab = visibleTabs.find((t) => t.id === tabId);

    // Covers a deep link to a 3D tab on a project that has the setting turned off — redirect to the
    // first tab rather than rendering an empty tab body.
    if (!activeTab) {
        return <ProjectRelativeRedirect to={`/options/${visibleTabs[0].url}`} />;
    }

    return (
        <>
            <SimplePageTitle title={t`Options`} />

            <Tabs.Container activeId={tabId} variant="underline" sticky>
                {visibleTabs.map((t) => (
                    <Tabs.Tab
                        key={t.id}
                        id={t.id}
                        as={Link}
                        to={`/projects/${projectId}/options/${t.url}`}
                        label={t.name}
                        badge={t.notificationCount ?? undefined}
                    />
                ))}
                <Tabs.Content>{activeTab.render({ projectId: +projectId })}</Tabs.Content>
            </Tabs.Container>
        </>
    );
};
